import { useEffect, useState } from 'react';
import { Modal, Form, Select, Alert, Tag, Spinner, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';
import { useDebounce } from '../../hooks/useDebounce';

interface Props {
  open: boolean;
  onClose: () => void;
  customerType: 'RETAILER' | 'FARMER';
  /** The record being merged AWAY (the duplicate). */
  duplicate: any;
}

/**
 * Merge this (duplicate) retailer/farmer into a surviving record: every reference (orders,
 * invoices, payments, ledger, visits, complaints…) re-points to the survivor, the duplicate's
 * outstanding folds in (retailers), and the duplicate is closed + soft-deleted.
 */
export function MergeCustomerModal({ open, onClose, customerType, duplicate }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const term = useDebounce(search, 350);
  const isRetailer = customerType === 'RETAILER';
  const dupName = duplicate?.companyName ?? duplicate?.fullName ?? 'this record';

  const { data, isFetching } = useQuery({
    queryKey: ['merge-survivor-search', customerType, term],
    queryFn: () => request<any>({ url: isRetailer ? '/retailers' : '/farmers', params: { search: term, pageSize: 20 } }),
    enabled: open && term.trim().length >= 2,
  });
  const options = (data?.data ?? [])
    .filter((r: any) => String(r.id) !== String(duplicate?.id))
    .map((r: any) => ({ value: String(r.id), label: isRetailer ? `${r.code} · ${r.companyName} (${r.mobile})` : `${r.fullName} (${r.mobile})` }));

  useEffect(() => { if (open) { form.resetFields(); setSearch(''); } }, [open, form]);

  const merge = useMutation({
    mutationFn: (v: any) => request({ url: '/territory-assignments/merge-customer', method: 'POST', data: { customerType, duplicateId: String(duplicate.id), survivorId: v.survivorId } }),
    onSuccess: (res: any) => {
      const tables = Object.keys(res?.repointed ?? {}).length;
      message.success(`Merged — references across ${tables} table(s) moved to the survivor`);
      qc.invalidateQueries({ queryKey: ['retailers'] });
      qc.invalidateQueries({ queryKey: ['farmers'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to merge'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Merge duplicate · ${dupName}`}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="danger" loading={merge.isPending} onClick={() => form.submit()}>Merge</Button>
      </>}>
      {duplicate && (
        <Form form={form} layout="vertical" onFinish={(v) => merge.mutate(v)}>
          <div style={{ marginBottom: 12 }}>
            Merge <Tag tone="red">{dupName}</Tag> into the survivor below. All of its orders, invoices, payments and history move to the survivor; this record is then closed.
          </div>
          <Form.Item name="survivorId" label="Survivor (the record to keep)" rules={[{ required: true, message: 'Pick the surviving record' }]}>
            <Select
              showSearch filterOption={false} onSearch={setSearch}
              placeholder={`Search ${isRetailer ? 'retailers' : 'farmers'} by name / mobile`}
              notFoundContent={isFetching ? <Spinner /> : (term.length < 2 ? 'Type at least 2 characters' : 'No matches')}
              options={options}
            />
          </Form.Item>
          <Alert type="warning" title="This can't be auto-undone. The duplicate is soft-deleted; its references and (for retailers) its outstanding balance move to the survivor." />
        </Form>
      )}
    </Modal>
  );
}
